export type TTrainingSettings = {
  isEnabled: boolean;
  /** When true, the daily report is generated as soon as the agent submits their EOD report; `dailyReportTime` acts as a fallback. */
  triggerDailyOnEod: boolean;
  /** IANA timezone, e.g. "America/New_York" */
  timezone: string;
  /** 24h "HH:mm" */
  dailyReportTime: string;
  /** 0 = Sunday … 6 = Saturday */
  weeklyReportDay: number;
  /** 24h "HH:mm" */
  weeklyReportTime: string;
};

/* ------------------------------------------------------------------ *
 * Training (AI Sales Coach) module types.
 * The shapes below are the UI-side contract the coaching hooks map the
 * /api/coaching responses into.
 * ------------------------------------------------------------------ */

export type ScoreDimension =
    | "listening"
    | "communication"
    | "discovery"
    | "objection"
    | "closing";

export type AgentRole = "Admin" | "Sales Manager" | "Sales Agent";
export type AgentSeniority = "Junior" | "Mid" | "Senior";
export type AgentStatus =
    | "Top performer"
    | "Improving"
    | "Steady"
    | "Needs attention"
    | "No data";

export type DimensionScore = {
    /** 0–10 — null when the dimension has no scored calls yet */
    score: number | null;
    /** change vs previous week (positive = improved) — null when there's no previous data */
    delta: number | null;
    /** top-20% benchmark for this dimension — null when the backend has no benchmark yet */
    topPerformer: number | null;
};

export type TrainingMomentKind =
    | "missed_opportunity"
    | "strong_handling"
    | "learn_from_peer";

export type TrainingMoment = {
    id: string;
    agentId: string;
    kind: TrainingMomentKind;
    /** Session id of the source call — null when the backend didn't provide one */
    recordingId: string | null;
    leadName: string | null;
    leadCompany: string | null;
    /** For learn_from_peer: the rep whose call this is */
    callOwner: { name: string; initials: string; color: string };
    /** mm:ss into the recording — null when unknown */
    timestamp: string | null;
    /** display date, e.g. "May 11" — null when unknown */
    callDate: string | null;
    /** Short one-line headline for this moment (optional; shown above description) */
    title?: string;
    description: string;
    tags: string[];
    score?: number;       // only for strong_handling
};

export type TrainingAgent = {
    id: string;
    name: string;
    initials: string;
    color: string;
    email: string;
    role: AgentRole;
    seniority?: AgentSeniority;
    daysWithCoach: number;
    // Performance
    /** 0–10 — null when the agent has no analyzed calls yet */
    score: number | null;
    /** change vs previous week — null when there's no previous data */
    scoreDelta: number | null;
    callsThisWeek: number;
    callsTotalAnalyzed: number;
    streakDays: number;
    teamRank: number;
    teamRankDelta: number;  // positive = moved up
    teamSize: number;
    /** false when the backend can't rank the agent — teamRank/teamRankDelta/teamSize should be ignored in the UI */
    teamRankAvailable: boolean;
    /** Backend reason code when teamRankAvailable is false (e.g. "no_manager_assigned") */
    teamRankUnavailableReason?: string | null;
    status: AgentStatus;
    /** human-readable label, e.g. "Discovery & needs" — null when no dimension has scores yet */
    currentFocus: string | null;
    currentFocusDim: ScoreDimension | null;
    /** Oldest → newest weekly score trend */
    trendWeeks: number[];
    /** Per-dimension breakdown */
    dimensions: Record<ScoreDimension, DimensionScore>;
};

/* ------------------------------------------------------------------ *
 * Score dimension catalog
 * ------------------------------------------------------------------ */

export const SCORE_DIMS: Array<{
    id: ScoreDimension;
    label: string;
    short: string;
    hint: string;
}> = [
    {
        id: "listening",
        label: "Listening & engagement",
        short: "Listening",
        hint: "Active listening, interruption rate, talk/listen ratio",
    },
    {
        id: "communication",
        label: "Communication quality",
        short: "Communication",
        hint: "Clarity, pacing, energy, jargon",
    },
    {
        id: "discovery",
        label: "Discovery & needs",
        short: "Discovery",
        hint: "Open-ended questions, pain exploration, qualification",
    },
    {
        id: "objection",
        label: "Objection handling",
        short: "Objection",
        hint: "Reframing, value-first responses, calm pacing",
    },
    {
        id: "closing",
        label: "Closing & outcome",
        short: "Closing",
        hint: "Concrete next steps, commitment, urgency",
    },
];

export function dimLabel(dim: ScoreDimension): string {
    return SCORE_DIMS.find((d) => d.id === dim)?.label ?? dim;
}

/* ------------------------------------------------------------------ *
 * Ask Your Coach — suggested questions (static UI copy)
 * ------------------------------------------------------------------ */

export type AskCoachPrompt = {
    id: string;
    question: string;
};

export const COACH_PROMPTS: AskCoachPrompt[] = [
    { id: "p-1", question: "Why did I drop in discovery this week?" },
    { id: "p-2", question: "Show me my best objection handling this month" },
    { id: "p-3", question: "How do I compare to my team?" },
    { id: "p-4", question: "What should I focus on tomorrow?" },
];

/* ------------------------------------------------------------------ *
 * Agent journey / ROI / coaching log (agent profile page)
 * ------------------------------------------------------------------ */

export type CoachingEventKind =
    | "plan_set"
    | "milestone"
    | "peer_review"
    | "focus_shift"
    | "streak";

export type WeeklyJourneySnapshot = {
    weekLabel: string;         // "Week 1"
    weekStart: string;
    overall: number;           // 0–10
    dimensions: Record<ScoreDimension, number>;
    callsAnalyzed: number;
};

export type JourneyCoachingEvent = {
    id: string;
    kind: CoachingEventKind;
    date: string;
    heading: string;
    body: string;
};

export type AgentJourney = {
    agentId: string;
    snapshots: WeeklyJourneySnapshot[];
    events: JourneyCoachingEvent[];
};

export type AgentRoiSummary = {
    agentId: string;
    scoreStart: number;
    scoreCurrent: number;
    scoreLift: number;
    callsAnalyzed: number;
    weeksActive: number;
    dealsInfluenced: number;
    revenueInfluenced: number;       // USD
    timeSavedHoursPerWeek: number;
    focusShifts: number;
};

/* ------------------------------------------------------------------ *
 * Coaching ROI dashboard — real API shapes (GET /api/coaching/roi)
 * ------------------------------------------------------------------ */

export type SkillLiftEntry = {
    key: string;
    label: string;
    current: number;
    baseline: number;
    lift: number;
};

export type TeamRoiMetrics = {
    currentAvgScore: number;
    previousAvgScore: number;
    weekOverWeekDelta: number;
    baselineAvgScore: number;
    scoreLift: number;
    scoreLiftPct: number;
    callsAnalyzed: number;
    reportsDelivered: number;
    snippetsShared: number;
    skillLift: SkillLiftEntry[];
};

export type AgentRoiEntry = {
    agentId: string;
    agentName: string;
    initials: string;
    color: string;
    currentAvgScore: number | null;
    previousAvgScore: number | null;
    weekOverWeekDelta: number | null;
    baselineAvgScore: number | null;
    scoreLift: number | null;
    callsAnalyzed: number;
    reportsDelivered: number;
    snippetsShared: number;
    hasData: boolean;
};

export type WeeklyMover = {
    agentId: string;
    agentName: string;
    initials: string;
    color: string;
    delta: number;
    currentAvgScore: number;
    previousAvgScore: number;
};

export type CoachingRoiData = {
    teamRoi: TeamRoiMetrics;
    agentRoi: AgentRoiEntry[];
    gainers: WeeklyMover[];
    decliners: WeeklyMover[];
    teamSize: number;
};

export type CoachingEventLogEntry = JourneyCoachingEvent & { agentId: string };

/* ------------------------------------------------------------------ *
 * Weekly report
 * ------------------------------------------------------------------ */

export type WeeklyReportSection = {
    heading: string;
    /** Supports **bold**, [[positive:...]], [[focus:...]] inline markers and \n\n paragraph breaks */
    body: string;
};

/* ------------------------------------------------------------------ *
 * AI Suggested Actions (Admin view — coaching action cards)
 * ------------------------------------------------------------------ */

export type SuggestedAction = {
    id: string;
    severity: "urgent" | "opportunity" | "attention";
    title: string;
    reasoning: string;
    evidence: {
        label: string;
        text: string;
    };
    primaryActionKind: "schedule_1to1" | "share_snippet" | "assign_training";
    payload: {
        agentId: string | null;
        agentName: string | null;
        skill: string | null;
        recordingId: string | null;
        trainingTopic: string | null;
    };
};

/* ------------------------------------------------------------------ *
 * Training action log (Admin view — recent activity)
 * ------------------------------------------------------------------ */

export type TrainingLogEntry = {
    id: string;
    agentId: string | null;
    kind: "1to1_scheduled" | "snippet_shared" | "training_assigned";
    text: string;
    when: string;
};

export const SEED_TRAINING_LOG: TrainingLogEntry[] = [
    {
        id: "log-seed-1",
        agentId: null,
        kind: "1to1_scheduled",
        text: "Coaching 1:1 scheduled — closing technique review.",
        when: "Yesterday · 4:30 PM",
    },
    {
        id: "log-seed-2",
        agentId: null,
        kind: "training_assigned",
        text: "Rapport Building Techniques assigned · due this week.",
        when: "Yesterday · 11:20 AM",
    },
    {
        id: "log-seed-3",
        agentId: null,
        kind: "snippet_shared",
        text: "Best call snippet shared to learning feed.",
        when: "Mon · 2:45 PM",
    },
];

export type WeeklyReport = {
    agentId: string;
    weekLabel: string;   // e.g. "Week of May 5–11"
    weekStart: string;
    weekEnd: string;
    generatedAt: string; // e.g. "May 11, 2025 at 11:00 PM"
    sections: WeeklyReportSection[];
    // Extended fields populated from the real API
    executiveSummary?: string;
    totalCalls?: number;
    weakestDimension?: ScoreDimension | null;
    strongestDimension?: ScoreDimension | null;
    talkListenSplit?: string | null;
    keyWins?: string[];
    thingsToWatch?: string[];
    focusNext?: string | null;
    comparisonScoreChange?: number | null;
};
